import { type ChangeEvent, type RefObject, type TextareaHTMLAttributes, forwardRef, useEffect, useId, useRef } from 'react';

import { cn } from '../utils/cn';

export interface TextAreaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'size'
> {
  /**
   * TextArea label
   */
  label?: string;

  /**
   * TextArea size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Helper text to display below textarea
   */
  helperText?: string;

  /**
   * Maximum character count
   */
  maxLength?: number;

  /**
   * Show character counter
   * @default false
   */
  showCount?: boolean;

  /**
   * Auto-resize textarea to fit content
   * @default false
   */
  autoResize?: boolean;

  /**
   * Full width textarea
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Whether the field is required
   * @default false
   */
  required?: boolean;

  /**
   * Custom container class name
   */
  containerClassName?: string;

  /**
   * Custom label class name
   */
  labelClassName?: string;
}

/**
 * TextArea component
 *
 * A versatile textarea component with character counting, auto-resize, validation, and accessibility.
 * Supports various sizes and states.
 *
 * @example
 * ```tsx
 * <TextArea
 *   label="Description"
 *   placeholder="Enter description..."
 *   maxLength={500}
 *   showCount
 * />
 *
 * <TextArea
 *   label="Comments"
 *   autoResize
 *   placeholder="Add your comments..."
 * />
 * ```
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      size = 'md',
      error,
      helperText,
      maxLength,
      showCount = false,
      autoResize = false,
      fullWidth = false,
      required = false,
      disabled,
      className,
      containerClassName,
      labelClassName,
      id,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const autoId = useId();
    const textareaId = id || `textarea-${autoId}`;
    const errorId = `${textareaId}-error`;
    const helperTextId = `${textareaId}-helper`;

    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const textareaRef = (ref as RefObject<HTMLTextAreaElement>) || internalRef;

    const currentLength =
      typeof value === 'string' ? value.length : props.defaultValue?.toString().length || 0;

    // Auto-resize functionality
    useEffect(() => {
      if (autoResize && textareaRef.current) {
        const textarea = textareaRef.current;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [value, autoResize, textareaRef]);

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e);

      if (autoResize && textareaRef.current) {
        const textarea = textareaRef.current;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    };

    const baseStyles = [
      'block w-full rounded-lg border',
      'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
      'transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-1 dark:focus:ring-offset-gray-900',
      'disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-500 dark:disabled:text-gray-600 disabled:cursor-not-allowed',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      'resize-none',
    ];

    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm min-h-[80px]',
      md: 'px-4 py-2 text-base min-h-[100px]',
      lg: 'px-5 py-3 text-lg min-h-[120px]',
    };

    const stateStyles = error
      ? [
          'border-error-500',
          'text-error-900 dark:text-error-200',
          'focus:ring-error-500',
          'focus:border-error-500',
        ]
      : [
          'border-gray-300 dark:border-gray-600',
          'focus:ring-primary-500',
          'focus:border-primary-500 dark:focus:border-primary-400',
        ];

    const isNearLimit = maxLength && currentLength / maxLength > 0.9;
    const isOverLimit = maxLength && currentLength > maxLength;

    return (
      <div className={cn(fullWidth ? 'w-full' : '', containerClassName)}>
        <div className="flex items-center justify-between mb-1">
          {label && (
            <label
              htmlFor={textareaId}
              className={cn(
                'block text-sm font-medium text-gray-700 dark:text-gray-300',
                disabled && 'text-gray-500 dark:text-gray-600',
                labelClassName
              )}
            >
              {label}
              {required && <span className="text-error-500 dark:text-error-400 ml-1">*</span>}
            </label>
          )}

          {showCount && maxLength && (
            <span
              className={cn(
                'text-sm',
                isOverLimit
                  ? 'text-error-600 dark:text-error-400 font-medium'
                  : isNearLimit
                    ? 'text-warning-600 dark:text-warning-400'
                    : 'text-gray-500 dark:text-gray-400'
              )}
            >
              {currentLength}/{maxLength}
            </span>
          )}
        </div>

        <textarea
          ref={textareaRef}
          id={textareaId}
          disabled={disabled}
          maxLength={maxLength}
          value={value}
          onChange={handleChange}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? errorId : helperText ? helperTextId : undefined}
          aria-required={required}
          className={cn(
            baseStyles,
            sizeStyles[size],
            stateStyles,
            autoResize && 'overflow-hidden',
            className
          )}
          {...props}
        />

        {error && (
          <p id={errorId} className="mt-1 text-sm text-error-600 dark:text-error-400" role="alert">
            {error}
          </p>
        )}

        {!error && helperText && (
          <p id={helperTextId} className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';
